// **The live-mode Beekeeper chat transport, and the pvp prototype's discovery read.**
//
// Prototype wiring for `docs/dev/pvp-prototype.md`, deliberately outside the `waggle-change`
// workflow: no delta spec, no `// covers:` markers. It is reachable only when the server
// advertises the prototype, and live mode without that flag is byte-for-byte what it was.
//
// **The transport is the rounds one, pointed at another prefix.** The server serves
// `/live/{colony}/chat/{messages,turns/{id}/events,turns/{id}/stop,thread}` with the *same*
// `TurnEventDto` frames, the same cursor semantics and the same `ThreadDto` body the rounds chat
// emits, so `ChatSseClient`, `turnEventFromJson`, `applyTurnEvent` and `createChatChannel` are
// all reused verbatim; the only difference expressible here is the first path segment and the
// credential, which is the live session token the WebSocket handshake already carries.
//
// `fetchColony` is a **no-op**. The rounds channel re-reads the colony after a turn cuts a version
// because a rounds sandbox's document only changes by request; in live mode the server pushes a
// fresh `ColonyMeta` over the WebSocket on every accepted hot-swap, so an HTTP re-read here would
// be a second, slower source for something the store already has. Returning `null` is exactly the
// contract's "nothing to apply" and the channel does nothing further with it.

import type { ChatTransport } from '../app/rounds/chat-channel';
import { ChatSseClient, type OpenTurnStream } from './chat-sse';

export interface LiveChatTransportConfig {
  /** Same origin as the live server in production — `''`, exactly like the lobby base. */
  baseUrl: string;
  /** The numeric live colony id, which is the chat scope. */
  colonyId: number;
  /** The live session token, hex — the same one `Hello` carries. Not a rounds bearer. */
  token: string;
  fetchImpl?: typeof fetch;
  openStream?: OpenTurnStream;
}

export interface PresentationPreviewChatTransportConfig {
  baseUrl: string;
  team: 'red' | 'blue';
  /** The short-lived presentation capability, never either player's session bearer. */
  presenterToken: string;
  fetchImpl?: typeof fetch;
  openStream?: OpenTurnStream;
}

/** A `ChatTransport` over the live pvp chat routes, so `createChatChannel` is reused unchanged. */
export function createLiveChatTransport(config: LiveChatTransportConfig): ChatTransport {
  const client = new ChatSseClient({
    baseUrl: config.baseUrl,
    pathPrefix: 'live',
    scope: String(config.colonyId),
    token: config.token,
    ...(config.fetchImpl === undefined ? {} : { fetchImpl: config.fetchImpl }),
    ...(config.openStream === undefined ? {} : { openStream: config.openStream }),
  });
  return {
    sendMessage: (text, targetType, handlers) => client.sendMessage(text, targetType, handlers),
    attachTurn: (turnId, afterCursor, handlers) => client.attachTurn(turnId, afterCursor, handlers),
    stopTurn: (turnId) => client.stopTurn(turnId),
    fetchThread: () => client.fetchThread(),
    clearThread: () => client.clearThread(),
    // See the header: the WebSocket already pushes `ColonyMeta`, so there is nothing to fetch.
    fetchColony: async () => null,
  };
}

/** Read-only access to the selected player's real in-memory chat. The client-side preview wraps
 * every committing method, and the server mounts only the two GET routes under this prefix — which
 * is why no `clearThread` is supplied here: an absent method is what makes the "New chat" control
 * absent from the preview rather than present and refused (`ChatSeam.clearConversation`). */
export function createPresentationPreviewChatTransport(
  config: PresentationPreviewChatTransportConfig,
): ChatTransport {
  const client = new ChatSseClient({
    baseUrl: config.baseUrl,
    pathPrefix: 'presentation/preview',
    scope: config.team,
    token: config.presenterToken,
    ...(config.fetchImpl === undefined ? {} : { fetchImpl: config.fetchImpl }),
    ...(config.openStream === undefined ? {} : { openStream: config.openStream }),
  });
  return {
    sendMessage: (text, targetType, handlers) => client.sendMessage(text, targetType, handlers),
    attachTurn: (turnId, afterCursor, handlers) => client.attachTurn(turnId, afterCursor, handlers),
    stopTurn: (turnId) => client.stopTurn(turnId),
    fetchThread: () => client.fetchThread(),
    fetchColony: async () => null,
  };
}

// --- discovery ------------------------------------------------------------------------------

/**
 * **The one place the pvp discovery flag's endpoint and name live.** Moving either is a one-line
 * change here.
 *
 * `waggle-server/src/router.rs` puts the flag on `GET /health`: the one route both game-mode arms
 * mount, unauthenticated, already existing, and answering JSON — its `pvp` field is a startup-time
 * configuration fact captured at mount, `false` on every deployment that has not turned the
 * prototype on.
 *
 * It is read **only on the boot path that already probes** (`app/rounds/game-mode.ts`'s
 * `resolveBootSurface`, chained after the mode probe resolves to live). A boot that selects its
 * mode explicitly — `?mode=live`, or a build-time `VITE_GAME_MODE` — deliberately makes no request
 * at all, and this must not be what breaks that: there, `?pvp=1` is the only signal.
 *
 * Absent-safe in every direction: a missing field, a non-boolean field, a non-JSON body, no body
 * at all, a non-2xx answer or an unreachable server all read as `false`, so an unclear signal
 * produces live mode's shipped composition and never a half-wired chat surface.
 */
export const PVP_PROBE_PATH = '/health';
const PVP_PROBE_FIELD = 'pvp';

/** Reads the pvp flag out of a probe response body. Anything that is not an explicit `true` is
 * `false`. */
export function pvpFromProbeBody(body: unknown): boolean {
  if (body === null || typeof body !== 'object') {
    return false;
  }
  return (body as Record<string, unknown>)[PVP_PROBE_FIELD] === true;
}

/** A per-load `?pvp=1` override, so the prototype's client half is drivable against a server that
 * has not shipped the flag yet, and against one whose probe an explicit `?mode=live` skipped.
 * `?pvp=0` forces it off. Absent, the probe body decides. */
export function pvpFromQuery(search: string): boolean | null {
  try {
    const raw = new URLSearchParams(search).get('pvp');
    if (raw === null) return null;
    return raw !== '0' && raw.toLowerCase() !== 'false';
  } catch {
    return null;
  }
}
